import React, { useEffect, useState } from "react";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Button, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveAsset from "./RemoveAsset";
import AssetFormModal from "./AssetFormModal";
import { toast } from 'react-toastify';
import './AssetManagement.scss';
import '../../assets/scss/tables.scss';


const AssetManagement = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [assets, setAssets] = useState([]);
    const [selectedAssetToDelete, setSelectedAssetToDelete] = useState(null)

    /* null = closed, {} = adding, {…} = editing that asset */
    const [assetInForm, setAssetInForm] = useState(null)

    const { fetchData } = useAxios();

    const tableRef = React.createRef();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        if (tableRef.current) tableRef.current.scrollTop = 0;
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    useEffect(() => {
        assetsList();
    }, []);

    const assetsList = async () => {
        const response = await fetchData({
            url: URLS.getAssetsList,
            method: "get",
        });
        /* The API returns items in the order they were added, so reverse
           for newest first. Copy before reversing — reverse() is in place. */
        setAssets([...(response?.Items || [])].reverse());
    };

    /* One save path for both adding and editing, so the popup does not have to
       know which endpoint it is talking to. Returns true when it worked. */
    const saveAsset = async (form, isEdit) => {
        const response = isEdit
            ? await fetchData({
                url: URLS.assetModify,
                method: "put",
                body: { assetId: form._id, ...form }
            })
            : await fetchData({
                url: URLS.createAsset,
                method: "post",
                body: { AssetsList: [form] }
            });

        if (response?.success === false) {
            toast.error(response?.message || "Could not save the asset");
            return false;
        }

        toast.success(response?.message || (isEdit ? "Asset updated" : "Asset added"));
        if (!isEdit) setPage(0);
        await assetsList();
        return true;
    };

    const handleDeleteAsset = async () => {
        if (!selectedAssetToDelete) return;

        const response = await fetchData({
            url: URLS.removeAsset,
            method: "put",
            body: { assetId: selectedAssetToDelete._id },
        });

        if (response?.success === false) {
            toast.error(response?.message || "Could not remove the asset");
            return;
        }

        toast.success("Asset removed");
        await assetsList();
        handleClose();
    }

    const handleClose = () => setSelectedAssetToDelete(null)

    return (
        <div className="row mb-1 pb-1 wh-table">
            <div className="col-sm-12  mb-2">
                <div className="wh-toolbar">
                    <h4 className="title">Asset Management</h4>
                    <Button variant="contained" className="wh-toolbar-action"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setAssetInForm({})}>
                        Add Asset
                    </Button>
                </div>
            </div>
            <div className="col-sm-12">
                <div className="card d-flex flex-column  pb-1" >
                    <TableContainer sx={{ maxHeight: 480, maxWidth: '100%' }} className='table-container-cls assets-table-cls' ref={tableRef}>
                        <Table stickyHeader aria-label="sticky table" className='table-cls' >
                            <TableHead>
                                <TableRow className='py-1'>
                                    <TableCell style={{ width: '80px' }}>S.No</TableCell>
                                    <TableCell style={{ minWidth: '180px' }}>Asset Name</TableCell>
                                    <TableCell style={{ minWidth: '150px' }}>Category</TableCell>
                                    <TableCell style={{ minWidth: '200px' }}>Make &amp; Model</TableCell>
                                    <TableCell style={{ minWidth: '240px' }}>Description</TableCell>
                                    <TableCell align="center" style={{ minWidth: '110px' }}>In Stock</TableCell>
                                    <TableCell align="center" style={{ minWidth: '100px' }}>Issued</TableCell>
                                    <TableCell align="center" style={{ minWidth: '110px' }}>Available</TableCell>
                                    <TableCell align="center" style={{ minWidth: '120px' }}>Returnable</TableCell>
                                    <TableCell className="sticky_right_header_column wh-actions-2" align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((eachAsset, i) => (
                                    <TableRow hover key={eachAsset.ItemName + i + 'assets'}>
                                        <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                                        <TableCell><span className="wh-pill">{eachAsset.ItemName}</span></TableCell>
                                        <TableCell>{eachAsset.category || '---'}</TableCell>
                                        <TableCell>
                                            <div className="wh-spec">
                                                <span>{[eachAsset.make, eachAsset.model].filter(Boolean).join(' ') || '---'}</span>
                                                {(eachAsset.processor || eachAsset.ram || eachAsset.modelYear) && (
                                                    <div className="wh-pills">
                                                        {eachAsset.processor && <span className="wh-pill wh-pill-sm">{eachAsset.processor}</span>}
                                                        {eachAsset.ram && <span className="wh-pill wh-pill-sm">{eachAsset.ram}</span>}
                                                        {eachAsset.modelYear && <span className="wh-pill wh-pill-sm wh-pill-quiet">{eachAsset.modelYear}</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="wh-cell-muted">{eachAsset.description || '---'}</TableCell>
                                        <TableCell align="center">{eachAsset.totalStock || 0}</TableCell>
                                        <TableCell align="center">{eachAsset.issued || 0}</TableCell>
                                        <TableCell align="center">
                                            {(() => {
                                                const available = eachAsset.available ?? Math.max((eachAsset.totalStock || 0) - (eachAsset.issued || 0), 0);
                                                const tone = available === 0 ? 'wh-pill-danger'
                                                    : available <= 2 ? 'wh-pill-warn' : 'wh-pill-good';
                                                return <span className={`wh-pill ${tone}`}>{available}</span>;
                                            })()}
                                        </TableCell>
                                        <TableCell align="center">
                                            {eachAsset.isReturnable === false
                                                ? <span className="wh-pill wh-pill-quiet">Keeps</span>
                                                : <span className="wh-pill wh-pill-good">Returns</span>}
                                        </TableCell>
                                        <TableCell className="sticky_right_column wh-actions-2" align="center">
                                            <div className="wh-actions-row">
                                                <Tooltip title='Edit asset' placement="top" arrow>
                                                    <IconButton aria-label="edit" onClick={() => setAssetInForm(eachAsset)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title='Delete asset' placement="top" arrow>
                                                    <IconButton className="wh-danger" aria-label="delete"
                                                        onClick={() => setSelectedAssetToDelete(eachAsset)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {assets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} className="wh-empty-cell">
                                            No assets yet. Add the first one to start issuing equipment.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 15, 20, 25, 50, 100]}
                        component="div"
                        count={assets.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        className='table-pagination-container'
                    />
                </div>
            </div>

            <AssetFormModal
                show={Boolean(assetInForm)}
                asset={assetInForm}
                onClose={() => setAssetInForm(null)}
                onSave={saveAsset}
            />

            {selectedAssetToDelete && <RemoveAsset asset={selectedAssetToDelete} handleDeleteAsset={handleDeleteAsset} handleClose={handleClose} />}
        </div >
    )
}

export default AssetManagement
